import Route from '@ember/routing/route';
import { set } from '@ember/object';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      route — resets the checkout wizard's per-visit state on entry (step,
 *              payment, promo code, notes, GDPR consent) so a repeat checkout in the
 *              same browser session doesn't inherit the previous order's leftovers.
 *   extends:   Route (@ember/routing/route)
 *   template:  none
 *   model:     - (no model hook; controller state only)
 *   key:       setupController
 *   uses:      application controller (collapses cart/menu chrome),
 *              controller.refreshContactTask (per-visit contact refresh)
 *   siblings:  controllers/checkout.js (the controller state this route resets)
 *   tests:     simple-routes-test.js ("checkout route wipes every per-visit residual",
 *              "checkout route deactivates every GDPR term option on entry",
 *              "checkout route refreshes the logged contact on entry")
 *   gotchas:   refreshContactTask is performed LAST: it early-returns when
 *              `_wrapContact` is still set, so it must run after the resets.
 *
 * Per-visit reset for the checkout wizard: step/payment/promo/notes/consent.
 */
export default class CheckoutRoute extends Route {
  setupController(controller /*, model*/) {
    const application = this.controllerFor('application');
    set(application, 'showCart', null);
    set(application, 'showMenu', null);
    set(controller, 'step', 1);
    set(controller, 'paymentAPI', null);
    set(controller, 'termAccepted', false);
    set(controller, 'paymentZero', false);
    // Clear per-checkout residuals so a repeat checkout in the same session does
    // not inherit the previous promo code / booking notes (maint 5.6 leak fix).
    set(controller, 'voucherCode', null);
    set(controller, 'showPromoCode', null);
    set(controller, 'notePrenotazione', null);
    set(controller, 'riepilogo', null);
    set(controller, 'checkTerms', false);
    set(controller, '_wrapContact', null);
    // the inline order-refusal reason and the cart «Removed · Undo» belong to one visit
    set(controller, 'checkoutFailure', null);
    set(controller, 'lastRemoved', null);

    if (Array.isArray(application.gdprField?.organization_term_options?.data)) {
      application.gdprField.organization_term_options.data.forEach((i) => set(i, 'active', false));
    }

    // Refresh the logged user's contact for this visit — AFTER the resets above,
    // so the task sees a cleared `_wrapContact`. Fire-and-forget on purpose: the
    // task consumes its own failures (it is what replaced the unconsumed
    // `contact.reload()` rejection of EMBER-SCUOLE-ECOMMERCE-2YQ) and the form
    // renders straight away from the already-loaded record.
    controller.refreshContactTask.perform();
  }
}
